<script setup lang="ts">
  import {
    ref,
    reactive,
    watch,
    onMounted,
    onBeforeUnmount,
    nextTick,
    computed,
    h,
    provide
  } from 'vue'
  import { pendingRefocusKey } from '@/shared/models/pendingRefocus'
  import Splitter from 'primevue/splitter'
  import SplitterPanel from 'primevue/splitterpanel'
  import { Card, Button, Drawer, Modal, message, Spin, Input } from 'ant-design-vue'
  import {
    PlusOutlined,
    CodeOutlined,
    HolderOutlined,
    ArrowUpOutlined,
    ArrowDownOutlined
  } from '@ant-design/icons-vue'
  import { Table } from './models/Table'
  import { TableSchema } from './models/TableSchema'
  import TableSelectionModal from './components/TableSelectionModal.vue'
  import { Download, Loader2, TableIcon, Trash2 } from 'lucide-vue-next'
  import TableConfiguration from './components/TableConfiguration.vue'
  import { TablesService } from './services/TablesService'
  import { defaultMessageDurationSeconds } from '@/utils'
  import TableJsonManagement from './components/TableJsonManagement.vue'
  import { debounceTime, Subject } from 'rxjs'
  import { TablesJsonParsingService } from './services/TablesJsonParsingService'
  import JsonTableConfiguration from './components/JsonTableConfiguration.vue'
  import { TablesJsonTableConfigurationService } from './services/TablesJsonTableConfigurationService'
  import { TablesTypeGuardService } from './services/TablesTypeGuardService'
  import { TablesSearchService } from './services/TableSearchService'
  import { TablesUiService } from './services/TablesUiService'
  import { ITablesApiService, TablesApiService } from './services/TablesApiService'
  import { InjectionService } from '@/shared/services/InjectionService'
  import { SchemaStoreService } from '@/shared/services/SchemaStoreService'
  import { TableSchemaUiFieldGroup } from './models/TableSchemaUi'
  import { useWindowSize } from '@/composables/useWindowSize'
  import { AplosFile } from '../FileManager/models/AplosFile'
  import { useAppLayoutMode } from '@/composables/useAppLayoutMode'
  import { AppLayoutMode } from '@/models/AppLayoutMode'
  import { AnalysisWizardModel } from '../AnalysisWizard/models/AnalysisWizardModel'
  import { Execution } from '@/shared/Executions/models/Execution'
  import { TableDataSourceType } from './models/TableDataSourceType'
  import { toast } from 'vue-sonner'
  import { useUser } from '@/composables/useUser'
  import {
    GetExecutionOutputFileArgs,
    GetExecutionOutputFileType
  } from '@/shared/Executions/models/GetExecutionOutputFileArgs'
  import { AnalysisWizardService } from '../AnalysisWizard/services/AnalysisWizardService'
  import { AnalysisWizardNavigationService } from '../AnalysisWizard/services/AnalysisWizardNavigationService'
  import {
    AplosFileApiService,
    IAplosFileApiService
  } from '../FileManager/services/AplosFileApiService'
  import { GetAplosFileArgs } from '../FileManager/models/GetAplosFileArgs'
  import { ExecutionPhaseType } from '@/shared/Executions/models/ExecutionPhaseType'
  import { QueueExecutionResponse } from '@/shared/Executions/models/QueueExecutionResponse'
  import { ExecutionStatusType } from '@/shared/Executions/models/ExecutionStatusType'
  import { WizardStepStatusType } from '@/shared/Wizard/models/WizardStep'
  import ExecutionSelectionTable from '../ExecutionHistory/components/ExecutionSelectionTable.vue'
  import { GetExecutionOutputPackageArgs } from '@/shared/Executions/models/GetExecutionOutputPackageArgs'
  import { downloadFile, extractFilename } from '@/utils/download'
  import { useFileCacheStore } from '@/composables/useFileCacheStore'
  import { GetFileDownloadUrlArgs } from '../FileManager/models/GetFileDownloadUrlArgs'
  import {
    ExecutionApiService,
    IExecutionApiService
  } from '@/shared/Executions/services/ExecutionApiService'
  import {
    ExecutionPhaseService,
    IExecutionPhaseService
  } from '@/shared/Executions/services/ExecutionPhaseService'
  import ExecutionStatusComponent from '@/shared/Executions/components/ExecutionStatusComponent.vue'
  import { CdnSchemaFactory } from '@/shared/CdnSchema/services/CdnSchemaFactory'
  import { CdnSchemaType } from '@/shared/CdnSchema/models/CdnSchemaType'

  interface ITablesProps {
    tables?: Array<Table>
    postProcessingExecution?: Execution
    analysisExecution?: Execution
    customCalculationExecution?: Execution
    analysisFile?: AplosFile
    cleanedFile?: AplosFile
    pkResultsFile?: AplosFile
    pkResultsCustomCalcsFile?: AplosFile
    wizardModel?: AnalysisWizardModel
    analysisSelectionDisabled?: boolean // in the wizard this component should rely on an input analysisExecution
    showJsonManagement?: boolean
  }

  const wizardNavigationService: AnalysisWizardNavigationService = InjectionService.injectStrict(
    AnalysisWizardNavigationService.injectableKey
  )

  const cdnSchemaFactory: CdnSchemaFactory = InjectionService.injectStrict(
    CdnSchemaFactory.injectableKey
  )

  const tablesApiService: ITablesApiService = InjectionService.injectStrict(
    TablesApiService.injectableKey
  )

  const tablesJsonParsingService: TablesJsonParsingService = InjectionService.injectStrict(
    TablesJsonParsingService.injectableKey
  )

  const executionApiService: IExecutionApiService = InjectionService.injectStrict(
    ExecutionApiService.injectableKey
  )

  const executionPhaseService: IExecutionPhaseService = InjectionService.injectStrict(
    ExecutionPhaseService.injectableKey
  )

  const aplosFileApiService: IAplosFileApiService = InjectionService.injectStrict(
    AplosFileApiService.injectableKey
  )

  const customSchemaStore = SchemaStoreService.useCustomSchemaStore()

  const props = defineProps<ITablesProps>()

  const emit = defineEmits<{
    (e: 'update:tables', value: Array<Table>): void
    (e: 'update:postProcessingExecution', value?: Execution): void
    (e: 'update:pkResultsFile', value?: AplosFile): void
    (e: 'update:pkResultsCustomCalcsFile', value?: AplosFile): void
    (e: 'update:showJsonManagement', value: boolean): void
  }>()

  const pendingRefocus = ref<{ levelFieldKey: string; cursor: number | null } | null>(null)
  provide(pendingRefocusKey, pendingRefocus)

  const executionStatusComponent = ref<InstanceType<typeof ExecutionStatusComponent> | null>(null)

  const executionName = ref<string>(props.postProcessingExecution?.name ?? '')
  const execution = ref<Execution | undefined>(props.postProcessingExecution)

  const isExecuting = computed(
    () =>
      [
        ExecutionStatusType.Succeeded,
        ExecutionStatusType.Cancelled,
        ExecutionStatusType.Failed,
        ExecutionStatusType.Unknown
      ].indexOf(execution.value?.status ?? ExecutionStatusType.Unknown) === -1
  )

  const isExecuted = computed(() => execution.value?.id != null)

  watch(
    () => props.postProcessingExecution,
    (val) => {
      execution.value = val
    },
    { immediate: true }
  )

  watch(
    () => executionName.value,
    (val) => {
      // invalidate existing execution if previously executed and use new instance to store name
      if (execution.value?.id || !execution.value) {
        execution.value = { name: val } as Execution
      } else if (execution.value) {
        execution.value.name = val
      }
      emit('update:postProcessingExecution', execution.value)
    },
    { immediate: false }
  )

  const auth = useUser()
  const fileCache = useFileCacheStore()

  const isLoadingFileContent = ref<boolean>(false)
  const isDownloadingResults = ref<boolean>(false)
  const isLoadingPkResultsFile = ref<boolean>(false)
  const selectedPkResultsFile = ref<AplosFile | undefined>(props.pkResultsFile)
  const isLoadingPkResultsCustomCalcsFile = ref<boolean>(false)
  const selectedPkResultsCustomCalcsFile = ref<AplosFile | undefined>(
    props.pkResultsCustomCalcsFile
  )
  const isLoadingAnalysisFile = ref<boolean>(false)
  const selectedAnalysisFile = ref<AplosFile | undefined>(props.analysisFile)
  const selectedCleanedFile = ref<AplosFile | undefined>(props.cleanedFile)

  const isLoadingAvailableTables = ref<boolean>(false)
  const hasErrorLoadingTables = ref<boolean>(false)
  const availableTableSchemas = ref<TableSchema[]>([])
  const activeTables = reactive<Table[]>([])
  const selectedTable = ref<Table | null>(null)
  const showTableSelection = ref(false)
  const showAnalysisSelectionModal = ref(false)
  const selectedAnalysisExecutionId = ref<string>()
  const selectedCustomCalculationExecutionId = ref<string>()
  const selectedDataSource = ref<{
    value: string
    label: string
    id: string
    type: TableDataSourceType
  }>()

  const parentExecutionId = computed(
    () => selectedCustomCalculationExecutionId.value ?? selectedAnalysisExecutionId.value
  )

  const showJsonManagement = ref(props.showJsonManagement)
  watch(
    () => props.showJsonManagement,
    (val) => {
      showJsonManagement.value = !!val
    }
  )
  watch(showJsonManagement, (val) => {
    emit('update:showJsonManagement', !!val)
  })

  const availableColumns = ref<Array<{ value: string; label: string }>>([])
  const availableParams = ref<Array<{ value: string; label: string }>>([])

  const availableDataSources = computed(() => {
    const sources: Array<{ value: string; label: string; id: string; type: TableDataSourceType }> =
      []

    if (selectedAnalysisFile?.value) {
      if (!selectedAnalysisFile.value?.name)
        throw Error(
          'Encountered selectedAnalysisFile with missing name when trying to populate availableDataSources!'
        )
      sources.push({
        value: selectedAnalysisFile.value?.name,
        label: selectedAnalysisFile.value?.name!,
        id: selectedAnalysisFile.value?.id!,
        type: TableDataSourceType.AnalysisInput
      })
    }

    if (selectedCleanedFile?.value) {
      if (!selectedCleanedFile.value?.name)
        throw Error(
          'Encountered selectedCleanedFile with missing name when trying to populate availableDataSources!'
        )
      sources.push({
        value: selectedCleanedFile.value?.name,
        label: selectedCleanedFile.value?.name,
        id: selectedCleanedFile.value?.id!,
        type: TableDataSourceType.CleanedData
      })
    }

    if (selectedPkResultsFile?.value) {
      if (!selectedPkResultsFile.value?.name)
        throw Error(
          'Encountered selectedPkResultsFile with missing name when trying to populate availableDataSources!'
        )
      sources.push({
        value: selectedPkResultsFile.value?.name,
        label: selectedPkResultsFile.value?.name,
        id: selectedPkResultsFile.value?.id!,
        type: TableDataSourceType.PkResults
      })
    }

    if (selectedPkResultsCustomCalcsFile?.value) {
      if (!selectedPkResultsCustomCalcsFile.value?.name)
        throw Error(
          'Encountered selectedPkResultsCustomCalcsFile with missing name when trying to populate availableDataSources!'
        )

      sources.push({
        value: selectedPkResultsCustomCalcsFile.value?.name,
        label: selectedPkResultsCustomCalcsFile.value?.name,
        id: selectedPkResultsCustomCalcsFile.value?.id!,
        type: TableDataSourceType.PkResultsCustomCalcs
      })
    }

    return sources
  })

  const cacheFileIfNeeded = async (fileType: TableDataSourceType, file?: AplosFile | undefined) => {
    if (!file?.id) return
    if (fileCache.has(fileType, file.id)) return

    try {
      isLoadingFileContent.value = true
      await AnalysisWizardService.runAsyncTaskWithLoading(
        'Downloading file content',
        props.wizardModel,
        async () => {
          await auth.initialize()
          const args: GetFileDownloadUrlArgs = {
            tenantId: auth.user?.tenant_id as string,
            userId: auth.user?.id as string,
            fileId: file.id as string
          }
          const downloadResp = await aplosFileApiService.getFileDownloadUrl(args)
          const downloadUrl = downloadResp?.data?.downloadUrl
          if (!downloadUrl) return
          const resp = await fetch(downloadUrl)
          if (!resp.ok) throw new Error('Failed to download file')
          const blob = await resp.blob()
          fileCache.set(fileType, file.id as string, blob)
        }
      )
    } catch {
      toast.error('An error was encoutered trying to download file content.')
    } finally {
      isLoadingFileContent.value = false
    }
  }

  // watch input props and ensure cached
  watch(
    [() => props.analysisFile, () => selectedAnalysisFile.value],
    ([propFile, selectedFile]) => {
      const fileToUse = selectedFile ?? propFile
      cacheFileIfNeeded(TableDataSourceType.AnalysisInput, fileToUse)
    },
    { immediate: true }
  )

  watch(
    [() => props.cleanedFile, () => selectedCleanedFile.value],
    ([propFile, selectedFile]) => {
      const fileToUse = selectedFile ?? propFile
      cacheFileIfNeeded(TableDataSourceType.CleanedData, fileToUse)
    },
    { immediate: true }
  )

  watch(
    [() => props.pkResultsFile, () => selectedPkResultsFile.value],
    ([propFile, selectedFile]) => {
      const fileToUse = selectedFile ?? propFile
      cacheFileIfNeeded(TableDataSourceType.PkResults, fileToUse)
    },
    { immediate: true }
  )

  watch(
    [() => props.pkResultsCustomCalcsFile, () => selectedPkResultsCustomCalcsFile.value],
    ([propFile, selectedFile]) => {
      const fileToUse = selectedFile ?? propFile
      cacheFileIfNeeded(TableDataSourceType.PkResultsCustomCalcs, fileToUse)
    },
    { immediate: true }
  )

  const loadAvailableColumnsAndParamCodes = (inputFile?: AplosFile) => {
    availableColumns.value = []
    availableParams.value = []

    let columnNames = inputFile?.metadata?.columnNames

    if (columnNames?.length) {
      // sort alphanumerically (natural numeric-aware ordering) then map
      const sortedCols = [...columnNames].sort((a, b) =>
        String(a)
          .trim()
          .localeCompare(String(b).trim(), undefined, { numeric: true, sensitivity: 'base' })
      )
      availableColumns.value = sortedCols.map((name) => ({ value: name, label: name }))
    }

    let paramCodes = inputFile?.metadata?.paramList
    if (paramCodes?.length) {
      // sort param list by paramcd alphanumerically then map
      const sortedParams = [...paramCodes].sort((a, b) =>
        String(a.paramcd).trim().localeCompare(String(b.paramcd).trim(), undefined, {
          numeric: true,
          sensitivity: 'base'
        })
      )
      availableParams.value = sortedParams.map((param) => ({
        value: param.paramcd,
        label: `${param.paramcd} (${param.param})`
      }))
    }
  }

  const setPkResultsFileForAnalysisExecution = async (
    analysisExecution: Execution | string
  ): Promise<AplosFile> => {
    const analysisExecutionId =
      typeof analysisExecution === 'string' ? analysisExecution : analysisExecution?.id

    if (!analysisExecutionId) {
      toast.error('A valid Analysis Execution is required to load PK Results File for Tables.')
    }

    try {
      isLoadingPkResultsFile.value = true
      await auth.initialize()
      const args = new GetExecutionOutputFileArgs({
        userId: auth.user.id as string,
        tenantId: auth.user.tenant_id as string,
        executionId: analysisExecutionId!,
        fileType: GetExecutionOutputFileType.PkResults
      })

      const pkResultsFileReponse = await AnalysisWizardService.runAsyncTaskWithLoading(
        'Loading Inputs',
        props.wizardModel,
        executionApiService.getExecutionOutput.bind(executionApiService),
        args
      )

      if (!pkResultsFileReponse || !pkResultsFileReponse.data) {
        toast.error('No PK Results file was returned for the given Analysis Execution.')
        throw new Error('PK Results file response is empty')
      }
      emit('update:pkResultsFile', pkResultsFileReponse.data)
      selectedPkResultsFile.value = pkResultsFileReponse.data
      return pkResultsFileReponse.data
    } catch (error) {
      toast.error('An error was encountered loading PK Results File for Tables.')
      throw error
    } finally {
      isLoadingPkResultsFile.value = false
    }
  }

  const setAnalysisFile = async (analysisFileId: string): Promise<AplosFile> => {
    try {
      isLoadingAnalysisFile.value = true
      await auth.initialize()

      const args = new GetAplosFileArgs({
        tenantId: auth.user.tenant_id as string,
        userId: auth.user.id as string,
        fileId: analysisFileId
      })

      const analysisFileResponse = await AnalysisWizardService.runAsyncTaskWithLoading(
        'Loading Inputs',
        props.wizardModel,
        aplosFileApiService.getFile.bind(aplosFileApiService),
        args
      )

      if (!analysisFileResponse || !analysisFileResponse.data) {
        toast.error('No Analysis file was returned for the given Analysis Execution.')
        throw new Error('PK Analysis file response is empty')
      }
      selectedAnalysisFile.value = analysisFileResponse.data
      return analysisFileResponse.data
    } catch (error) {
      toast.error('An error was encountered loading Analysis File for Tables.')
      throw error
    } finally {
      isLoadingAnalysisFile.value = false
    }
  }

  const setPkResultsCustomCalcsFileForCustomCalculationExecution = async (
    customcalculationsExecution: Execution | string
  ): Promise<AplosFile> => {
    const customCalculationExecutionId =
      typeof customcalculationsExecution === 'string'
        ? customcalculationsExecution
        : customcalculationsExecution?.id

    if (!customCalculationExecutionId) {
      toast.error(
        'A valid Custom Calculation Execution is required to load PK Results Custom Calcs File for Tables.'
      )
    }

    try {
      isLoadingPkResultsCustomCalcsFile.value = true
      await auth.initialize()
      const args = new GetExecutionOutputFileArgs({
        userId: auth.user.id as string,
        tenantId: auth.user.tenant_id as string,
        executionId: customCalculationExecutionId!,
        fileType: GetExecutionOutputFileType.PkResultsCustomCalcs
      })

      const pkResultsCustomCalcsFileReponse = await AnalysisWizardService.runAsyncTaskWithLoading(
        'Loading Inputs',
        props.wizardModel,
        executionApiService.getExecutionOutput.bind(executionApiService),
        args
      )

      if (!pkResultsCustomCalcsFileReponse || !pkResultsCustomCalcsFileReponse.data) {
        toast.error(
          'No PK Results Custom Calcs file was returned for the given Custom Calculation Execution.'
        )
        throw new Error('PK Results Custom Calcs file response is empty')
      }

      selectedPkResultsCustomCalcsFile.value = pkResultsCustomCalcsFileReponse.data
      emit('update:pkResultsCustomCalcsFile', pkResultsCustomCalcsFileReponse.data)
      return pkResultsCustomCalcsFileReponse.data
    } catch (error) {
      toast.error('An error was encountered loading PK Results Custom Calcs File for Tables.')
      throw error
    } finally {
      isLoadingPkResultsCustomCalcsFile.value = false
    }
  }

  const isProcessingTables = ref(false)
  const draggedTable = ref<Table | null>(null)
  const highlightedTableId = ref<string | null>(null)
  const deletingTableId = ref<string | null>(null)
  const dragOverIndex = ref<number>(-1)

  const activeTableTabKey = ref<string>('view')
  const activeTableViewMode = ref<{ mode: 'view' | 'edit' }>({ mode: 'view' })
  const activeTableEditConfiguration = ref<{} | null>(null)

  watch(
    selectedTable,
    (newVal) => {
      if (newVal) {
        const flattenedTables = TablesService.getFlattenedTables([newVal])
        activeTableEditConfiguration.value = flattenedTables?.length ? flattenedTables[0] : null
      }
    },
    { deep: false }
  )

  const onTableViewModeTabChange = (activeMode: 'view' | 'edit') => {
    if (selectedTable.value == null || activeTableEditConfiguration.value == null) return
    if (activeMode === 'edit') {
      const flattenedTables = TablesService.getFlattenedTables([selectedTable.value])
      activeTableEditConfiguration.value = flattenedTables?.length ? flattenedTables[0] : null
    }
  }

  const loadAvailableTableSchemas = async () => {
    hasErrorLoadingTables.value = false
    isLoadingAvailableTables.value = true
    try {
      const tableSchemas = await cdnSchemaFactory.loadAvailableSchemasForCdnSchemaType(
        CdnSchemaType.TableSchema,
        customSchemaStore
      )
      availableTableSchemas.value = tableSchemas
    } catch (error) {
      hasErrorLoadingTables.value = true
      message.error('Failed to load available tables.')
    } finally {
      isLoadingAvailableTables.value = false
    }
  }

  const addTable = (tableSchema: TableSchema): Table => {
    const newTable = TablesService.createTableFromTableSchema(tableSchema)
    activeTables.push(newTable)
    selectedTable.value = newTable
    updateSharedModelOnTableChange()
    message.success(`Added table: ${tableSchema.display_name}`)
    return newTable
  }

  const selectTable = (table: Table) => {
    selectedTable.value = table
  }

  const removeTableTimeoutMs: number = 600
  const removeTable = (table: Table) => {
    animateTableDeletion(table.id as string)
    message.success(`Removed "${table.display_name}"`)
  }

  const animateTableDeletion = (tableId: string) => {
    deletingTableId.value = tableId
    setTimeout(() => {
      const idx = activeTables.findIndex((t) => t.id === tableId)
      if (idx > -1) activeTables.splice(idx, 1)
      if (selectedTable.value?.id === tableId) {
        if (activeTables?.length > 0) {
          selectedTable.value = activeTables[0]
        } else {
          selectedTable.value = null
        }
      }
      deletingTableId.value = null
      updateSharedModelOnTableChange()
    }, removeTableTimeoutMs)
  }

  const notifyConfigChangedStream$ = new Subject<void>()
  const batchNotifyConfigChangedSubscription = notifyConfigChangedStream$
    .pipe(debounceTime(1800))
    .subscribe(() => {
      message.success('JSON updated', defaultMessageDurationSeconds)
    })

  // Handle file selection from Execution History
  const handleAnalysisExecutionSelected = async (
    analysisExecution: Execution,
    customCalculationExecution?: Execution | string
  ) => {
    showAnalysisSelectionModal.value = false
    const primaryAnalysisExecutionPhase =
      executionPhaseService.tryGetPrimaryPhaseTypeForExecution(analysisExecution)
    if (primaryAnalysisExecutionPhase !== ExecutionPhaseType.NCAAnalysis) {
      toast.error('Only NCA Analysis executions may be used in configuring tables!')
      //return
    }

    if (customCalculationExecution != null && typeof customCalculationExecution !== 'string') {
      const primaryCustomCalculationExecutionPhase =
        executionPhaseService.tryGetPrimaryPhaseTypeForExecution(customCalculationExecution)
      if (primaryCustomCalculationExecutionPhase !== ExecutionPhaseType.CustomCalculations) {
        toast.error(
          'Only a secondary Custom Calculation execution may be used in configuring tables!'
        )
        //return
      }

      selectedCustomCalculationExecutionId.value = customCalculationExecution?.id
    }

    if (!selectedAnalysisFile && !analysisExecution?.input?.fileId) {
      toast.error(
        'No input analysisFile was supplied and selected execution does not contain an input file!'
      )
      return
    }

    selectedAnalysisExecutionId.value = analysisExecution?.id

    const promises: Array<Promise<void>> = []

    if (!selectedPkResultsFile.value) {
      promises.push(
        (async () => {
          await setPkResultsFileForAnalysisExecution(analysisExecution)
        })()
      )
    }

    if (!selectedAnalysisFile.value) {
      promises.push(
        (async () => {
          await setAnalysisFile(analysisExecution.input?.fileId!)
        })()
      )
    }

    if (customCalculationExecution != null && !selectedPkResultsCustomCalcsFile.value) {
      promises.push(
        (async () => {
          await setPkResultsCustomCalcsFileForCustomCalculationExecution(customCalculationExecution)
        })()
      )
    }

    await Promise.all(promises)

    //TODO preset a data source or rely on user input?
    return
  }

  const clearAnalysisSelection = () => {
    selectedAnalysisExecutionId.value = undefined
    selectedPkResultsFile.value = undefined
    selectedPkResultsCustomCalcsFile.value = undefined
    selectedAnalysisFile.value = undefined
    availableColumns.value = []
    availableParams.value = []

    if (activeTables?.length > 0) {
      Modal.confirm({
        title: 'Confirm',
        content: 'Clear existing tables?',
        okText: 'Yes',
        cancelText: 'No',
        onOk() {
          activeTables.splice(0, activeTables.length)
          selectedTable.value = null
        },
        onCancel() {}
      })
    }
  }

  // Initialize flatRules or load for cleanedFileId and
  const initializeFlatTables = (flatTables?: Array<Table>) => {
    try {
      isLoadingAvailableTables.value = true
      const initializedTables: Array<Table> = []
      let inputTables = flatTables || []
      inputTables.forEach((flatTable: Table) => {
        const initializedTable = tablesJsonParsingService.initializeEntity(
          flatTable,
          availableTableSchemas.value,
          1
        )
        initializedTables.push(initializedTable)
      })
      // Replace the contents of the reactive array so Vue picks up the change
      activeTables.splice(0, activeTables.length, ...initializedTables)
      // Set the first rule as active
      if (activeTables.length > 0) selectedTable.value = activeTables[0]
    } catch {
      message.error('Failed to initialize tables.')
    } finally {
      isLoadingAvailableTables.value = false
    }
  }

  watch(
    () => props.analysisExecution,
    async (analysisExecution?: Execution) => {
      if (analysisExecution) {
        // for wizard integration we rely on calcs execution from shared model
        handleAnalysisExecutionSelected(analysisExecution, props.customCalculationExecution)
      }
    },
    { immediate: true }
  )

  const onSubmitTablesForGenerationClicked = async () => {
    if (props.wizardModel && execution.value?.id != null) {
      Modal.confirm({
        title: 'Confirm',
        content: 'Re-executing Tables will reset any Post Processing Configuration.',
        okText: 'Yes',
        cancelText: 'No',
        onOk: () => {
          submitTablesForGeneration()
        },
        onCancel() {}
      })
    } else {
      submitTablesForGeneration()
    }
  }

  const submitTablesForGeneration = async () => {
    isProcessingTables.value = true

    try {
      const auth = useUser()
      await auth.initialize()

      const flattenedTables = TablesService.getFlattenedTables(activeTables)

      const args = TablesService.createQueueTablesArgs(
        selectedAnalysisExecutionId.value as string,
        flattenedTables,
        executionName.value
      )

      emit('update:tables', args.tables)

      const response = await AnalysisWizardService.runAsyncTaskWithLoading(
        'Generating Tables',
        props.wizardModel,
        tablesApiService.processTables.bind(tablesApiService),
        auth?.user?.id as string,
        auth?.user?.tenant_id as string,
        args
      )

      const processTablesResponse = response?.data as QueueExecutionResponse
      if (!processTablesResponse?.executionId) throw Error('An unexpected error has occurred!')
      const successMessage = 'Generating Tables'
      /* processTablesResponse?.message?.length
          //? processTablesResponse.message
          : 'Tables queued for processing' */
      message.success(successMessage, defaultMessageDurationSeconds)

      execution.value = {
        id: processTablesResponse?.executionId,
        status: ExecutionStatusType.Queued,
        startedUtc: new Date().toISOString()
      } as Execution

      // Emit initial execution step so implementor (wizard) is updated
      emit('update:postProcessingExecution', execution.value)

      nextTick(() => {
        executionStatusComponent.value?.startPollingStatus()
      })
    } catch (error: any) {
      isProcessingTables.value = false
      const errors: Array<string> = error?.response?.data?.errors?.map((x: any) => x.error) || [
        'No specific errors encountered.'
      ]
      Modal.error({
        title: 'Error',
        content: h('div', { style: { marginTop: '5px' } }, [
          h(
            'p',
            { style: { marginBottom: '10px' } },
            'An error(s) was encountered generating tables:'
          ),
          h(
            'ul',
            errors.map((err) => h('li', { style: { marginTop: '5px' } }, err))
          )
        ]),
        okText: 'Ok',
        onOk() {}
      })
    }
  }

  //@ts-ignore
  const onExecutionStatusChanged = (status: ExecutionStatusType, ex: Execution) => {
    execution.value = ex
    emit('update:postProcessingExecution', ex)
  }

  const onExecutionCompleted = async (success: boolean, ex: Execution) => {
    execution.value = ex
    emit('update:postProcessingExecution', ex)
    isProcessingTables.value = false
    if (success) {
      updateWizardStepStatus(WizardStepStatusType.Good, 'Good')
    } else {
      message.error('An error was encountered generating tables.')
      updateWizardStepStatus(WizardStepStatusType.Errors, 'Error Generating Tables')
    }
  }

  const downloadResultsButtonDisabled = computed(
    () =>
      isProcessingTables.value ||
      execution.value?.id == null ||
      execution.value?.status !== ExecutionStatusType.Succeeded
  )

  const downloadResults = async () => {
    try {
      isDownloadingResults.value = true
      await auth.initialize()
      const args = new GetExecutionOutputPackageArgs({
        userId: auth.user.id as string,
        tenantId: auth.user.tenant_id as string,
        executionId: execution.value?.id as string
      })
      const response = await executionApiService.getExecutionOutputPackage(args)
      if (!response.downloadUrl)
        throw Error('Invalid downloadUrl encountered onDownloadActionClicked')
      await downloadFile(response.downloadUrl, extractFilename(response.downloadUrl))
    } catch (error) {
      console.log(error)
      toast.error(`An error occurred trying to download the output package.`)
    } finally {
      isDownloadingResults.value = false
    }
  }

  const moveTableUp = (table: Table) => {
    const currentIndex = activeTables.findIndex((t) => t.id === table.id)
    if (currentIndex > 0) {
      const temp = activeTables[currentIndex]
      activeTables[currentIndex] = activeTables[currentIndex - 1]
      activeTables[currentIndex - 1] = temp
      highlightMovedTable(table.id as string)
      message.success(`Moved "${table.display_name}" up to position ${currentIndex}`)
    }
  }

  const moveTableDown = (table: Table) => {
    const currentIndex = activeTables.findIndex((t) => t.id === table.id)
    if (currentIndex < activeTables.length - 1) {
      const temp = activeTables[currentIndex]
      activeTables[currentIndex] = activeTables[currentIndex + 1]
      activeTables[currentIndex + 1] = temp
      highlightMovedTable(table.id as string)
      message.success(`Moved "${table.display_name}" down to position ${currentIndex + 2}`)
    }
  }

  const highlightMovedTable = (tableId: string) => {
    highlightedTableId.value = tableId
    setTimeout(() => {
      highlightedTableId.value = null
    }, 1500)
  }

  // Drag and drop handlers
  const handleDragStart = (event: DragEvent, table: Table) => {
    draggedTable.value = table
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', table.id as string)
    }
  }

  const handleDragOver = (event: DragEvent, index: number) => {
    event.preventDefault()
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move'
    }
    dragOverIndex.value = index
  }

  const handleDragLeave = () => {
    dragOverIndex.value = -1
  }

  const handleDrop = (event: DragEvent, targetIndex: number) => {
    event.preventDefault()
    if (draggedTable.value) {
      const draggedIndex = activeTables.findIndex((t) => t.id === draggedTable.value!.id)
      if (draggedIndex !== targetIndex) {
        const [movedTable] = activeTables.splice(draggedIndex, 1)
        activeTables.splice(targetIndex, 0, movedTable)
        highlightMovedTable(draggedTable.value.id as string)
        message.success(`Moved "${draggedTable.value.display_name}" to position ${targetIndex + 1}`)
      }
    }
    draggedTable.value = null
    document.querySelectorAll('.drop-zone-active').forEach((el) => {
      el.classList.remove('drop-zone-active')
    })
  }

  const handleDragEnd = () => {
    draggedTable.value = null
    dragOverIndex.value = -1
  }

  const handleJsonEditorConfigurationChange = () => {
    if (selectedTable.value != null && activeTableEditConfiguration.value != null) {
      const editConfigurationJson = JSON.stringify([activeTableEditConfiguration.value])
      const result = tablesJsonParsingService.tryParseEntitiesFromJson(
        editConfigurationJson,
        availableTableSchemas.value
      )
      if (!result.valid) {
        message.error(
          `Error parsing JSON: ${result.errors?.join(', ')}`,
          defaultMessageDurationSeconds
        )
      } else {
        const existingTableIndex = activeTables.findIndex((x) => x.id === selectedTable.value?.id)
        if (
          !Array.isArray(result.parsedEntities) ||
          !result.parsedEntities?.length ||
          existingTableIndex <= -1
        )
          throw new Error('Error updating tables from Json Table Editor value!')
        const updatedTable = (result.parsedEntities as Array<Table>)[0]
        const existingTable = activeTables[existingTableIndex]
        Object.assign(existingTable, { ...updatedTable, id: existingTable.id })
        selectedTable.value = existingTable
      }
    }
  }

  //@ts-ignore
  const handleGeneratingRowsChange = (value: boolean) => {}

  // Update available columns and paramcds for selected well-known datasource type
  const handleDataSourceSelected = (value: {
    value: string
    label: string
    id: string
    type: TableDataSourceType
  }) => {
    selectedDataSource.value = value

    if (!value?.type) return

    switch (value.type) {
      case TableDataSourceType.AnalysisInput:
        loadAvailableColumnsAndParamCodes(selectedAnalysisFile?.value)
        break
      case TableDataSourceType.CleanedData:
        loadAvailableColumnsAndParamCodes(selectedCleanedFile?.value)
        break
      case TableDataSourceType.PkResults:
        loadAvailableColumnsAndParamCodes(selectedPkResultsFile?.value)
        break
      case TableDataSourceType.PkResultsCustomCalcs:
        loadAvailableColumnsAndParamCodes(selectedPkResultsCustomCalcsFile?.value)
        break
    }
  }

  const shouldClearPostProcessingExecution = computed(() => isExecuted.value && !isExecuting.value)

  // Ensure validation logic for wizard nav updated based on realtime shared model
  const updateSharedModelOnTableChange = () => {
    const postProcessingExecution = shouldClearPostProcessingExecution.value
      ? undefined
      : execution.value
    emit('update:postProcessingExecution', postProcessingExecution)
    const flattendTables = TablesService.getFlattenedTables(activeTables)
    emit('update:tables', flattendTables)
  }

  const handleConfigurationUpdate = async (
    table: Table,
    config: Record<string, any>,
    tableTypeChange?: boolean
  ) => {
    const tableSearchResults = TablesSearchService.findTableByIdRecursive(
      activeTables,
      table.id as string
    )
    if (tableSearchResults != null) {
      const topLevelParentTableIndex = activeTables.findIndex(
        (t) => t.id === tableSearchResults.topLevelParent?.id
      )
      if (topLevelParentTableIndex > -1) {
        if (tableTypeChange === true) {
          const newTableSchema = TablesService.tryGetTableSchemaByType(
            table._type,
            availableTableSchemas.value
          )
          if (newTableSchema == null)
            throw new Error(`Error getting TableSchema for table.type: ${table.type}!`)
          selectedTable.value = null
          let newTable = TablesService.createTableFromTableSchema(newTableSchema)
          newTable.description = table.configuration?.description
          newTable.configuration = { ...config }
          if (
            tableSearchResults.topLevelParent != null &&
            tableSearchResults.topLevelParent !== tableSearchResults.table &&
            tableSearchResults.path?.length > 1
          ) {
            throw new Error('Tables containing child tables is not supported.')
          } else {
            activeTables.splice(topLevelParentTableIndex, 1, newTable)
          }
          notifyConfigChangedStream$.next()
          nextTick(() => {
            selectedTable.value = activeTables[topLevelParentTableIndex]
          })
        } else if (tableSearchResults.topLevelParent === tableSearchResults.table) {
          activeTables[topLevelParentTableIndex].configuration = { ...config }
          activeTables[topLevelParentTableIndex]._type = config._type
          //activeTables[topLevelParentTableIndex].id = TablesService.generateTableId()
          notifyConfigChangedStream$.next()
        }

        updateSharedModelOnTableChange()
      }
    }
  }

  const onJsonManagementTablesApplied = () => {
    selectedTable.value = activeTables?.length ? activeTables[0] : null
    updateSharedModelOnTableChange()
  }

  const uiFieldGroups = computed(() => {
    return TablesUiService.getTableSchemaUiFieldGroups(selectedTable.value?.schema)
  })

  // Ensure first tab is selected on update of selected calculation
  // persist JSON tab selection if already active
  watch(
    [selectedTable, uiFieldGroups],
    ([calc, groups]) => {
      if (calc != null && groups != null && groups.length > 0) {
        // If on 'edit' tab, do not change
        if (activeTableTabKey.value === 'edit') return
        // If the current tab key is not in the group, or empty, set to first group
        if (!groups.some((g: TableSchemaUiFieldGroup) => g.name === activeTableTabKey.value)) {
          activeTableTabKey.value = groups[0].name
        }
      }
    },
    { immediate: true }
  )

  // For custom or unknown types disable superfluous input tab and just rely on Json Editor Tab
  const inputTabDisabled = computed(() => {
    if (
      selectedTable.value == null ||
      selectedTable.value._type === TablesService.CUSTOM_TABLE_TYPE
    )
      return true
    const activeSchemaType = TablesService.tryGetTableSchemaByType(
      selectedTable.value._type,
      availableTableSchemas.value
    )
    return activeSchemaType == null || activeSchemaType.type === TablesService.CUSTOM_TABLE_TYPE
  })

  // Ensure we preselect the edit tab if view is disabled
  // but only if the mode is not already edit
  watch(
    inputTabDisabled,
    (newValue) => {
      if (newValue) {
        activeTableViewMode.value = { mode: 'edit' }
      } else if (activeTableViewMode.value.mode !== 'edit') {
        activeTableViewMode.value = { mode: 'view' }
      }
    },
    { immediate: true }
  )

  const updateWizardStepStatus = (statusType?: WizardStepStatusType, statusMessage?: string) => {
    if (props.wizardModel != null) {
      const stepStatusType =
        statusType != null
          ? statusType
          : props.wizardModel.sharedModel?.postProcessingExecution?.id == null
            ? WizardStepStatusType.Warnings
            : WizardStepStatusType.Good

      let stepStatusMessage = statusMessage

      if (
        stepStatusMessage == null &&
        props.wizardModel.sharedModel?.postProcessingExecution?.id == null
      ) {
        stepStatusMessage = 'Tables not executed'
      }

      wizardNavigationService.trySetWizardActiveStepStatus(
        props.wizardModel,
        stepStatusType,
        stepStatusMessage
      )
    }
  }

  onMounted(async () => {
    await loadAvailableTableSchemas()

    updateWizardStepStatus()

    // Watch input flat rules and ensure initialized (after schemas loaded)
    initializeFlatTables(props.tables)

    watch(
      activeTables,
      () => {
        updateWizardStepStatus(WizardStepStatusType.Changes, 'Unsaved changes')
      },
      { immediate: false }
    )
  })

  onBeforeUnmount(() => {
    batchNotifyConfigChangedSubscription?.unsubscribe()
  })

  // --- Add this watcher to reset tab selection on selectedTable change ---
  watch(selectedTable, (newTable, oldTable) => {
    if (newTable && newTable !== oldTable) {
      if (uiFieldGroups.value && uiFieldGroups.value.length > 0) {
        activeTableTabKey.value = uiFieldGroups.value[0].name
      } else if (!inputTabDisabled.value) {
        activeTableViewMode.value = { mode: 'view' }
      } else {
        activeTableViewMode.value = { mode: 'edit' }
      }
    }
  })

  const { isExtraLargeTierOrLess } = useWindowSize()
  // derive live indicator from shared layoutMode so component follows active AppLayoutMode
  const { layoutMode } = useAppLayoutMode()
  const isFitToBrowser = computed(() => layoutMode.value === AppLayoutMode.FitToBrowser)

  defineExpose({
    activeTables,
    addTable,
    removeTable
  })
</script>

<template>
  <div
    :class="[
      'tables-container',
      'h-full',
      'min-h-0',
      {
        'fit-to-browser': isFitToBrowser,
        'h-full': isFitToBrowser,
        'tables-fill': isFitToBrowser
      }
    ]"
  >
    <Splitter class="h-full">
      <SplitterPanel :size="30" style="min-width: 350px">
        <div class="flex h-full flex-col border-r-2 shadow-sm">
          <!-- File Selection UI -->
          <div class="border-b p-4 shadow-sm">
            <div class="mb-3" v-if="!analysisSelectionDisabled">
              <label class="mb-1 block text-sm font-medium">Source Analysis</label>
              <div class="mb-2 flex items-center gap-2">
                <!-- Select Analysis -->
                <Button
                  type="dashed"
                  class="file-select-button min-w-0 flex-1 text-left"
                  :disabled="
                    isLoadingFileContent ||
                    isLoadingPkResultsFile ||
                    isLoadingPkResultsCustomCalcsFile ||
                    isLoadingAnalysisFile ||
                    isLoadingAvailableTables
                  "
                  block
                  @click="showAnalysisSelectionModal = true"
                  :title="
                    selectedAnalysisExecutionId ? 'Change selected Anaylsis' : 'Select an Analysis'
                  "
                >
                  <span class="file-select-label block w-full truncate">
                    {{ selectedAnalysisExecutionId ?? 'Select Analysis' }}
                  </span>
                </Button>

                <a-tooltip>
                  <template #title>
                    {{ 'Clear selection' }}
                  </template>
                  <Button
                    v-if="
                      selectedAnalysisExecutionId &&
                      !isLoadingPkResultsFile &&
                      !isLoadingFileContent
                    "
                    type="default"
                    shape="circle"
                    size="large"
                    danger
                    class="flex items-center justify-center"
                    @click="clearAnalysisSelection"
                    title="Clear Selection"
                    tooltip="Clear Selection"
                  >
                    <Trash2 class="text-base" style="line-height: 1; margin: 0" />
                  </Button>
                </a-tooltip>
              </div>
            </div>

            <!-- Post Processing Wizard Plots/Tables component select -->
            <slot v-if="wizardModel" name="wizardContentSelect" />

            <div class="mb-3">
              <label class="mb-1 block text-sm font-medium">Name</label>
              <a-tooltip placement="right">
                <template #title>
                  {{ 'Execution Name' }}
                </template>
                <Input
                  v-model:value="executionName"
                  size="large"
                  :disabled="
                    selectedAnalysisExecutionId == null ||
                    isLoadingAvailableTables ||
                    isLoadingAnalysisFile ||
                    isLoadingPkResultsFile ||
                    isLoadingPkResultsCustomCalcsFile ||
                    isLoadingFileContent
                  "
                  placeholder="Execution Name"
                />
              </a-tooltip>
            </div>

            <div class="flex items-center justify-between">
              <div>
                <h3 class="text-lg font-semibold">Tables</h3>
                <p class="mt-1 text-sm">
                  {{ activeTables.length }}
                  {{ activeTables.length === 1 ? 'table' : 'tables' }} configured
                </p>
              </div>
              <div class="flex items-center gap-2 self-center">
                <a-tooltip>
                  <template #title>
                    {{ 'Add New Table' }}
                  </template>
                  <Button
                    :disabled="
                      selectedAnalysisExecutionId == null ||
                      isLoadingAvailableTables ||
                      isLoadingAnalysisFile ||
                      isLoadingPkResultsFile ||
                      isLoadingPkResultsCustomCalcsFile ||
                      isLoadingFileContent
                    "
                    type="primary"
                    shape="circle"
                    size="large"
                    class="flex items-center justify-center"
                    @click="showTableSelection = true"
                    title="Add New Table"
                  >
                    <PlusOutlined class="text-base" style="line-height: 1; margin: 0" />
                  </Button>
                </a-tooltip>
                <a-tooltip>
                  <template #title>
                    {{ 'View/Export JSON' }}
                  </template>
                  <Button
                    type="default"
                    shape="circle"
                    size="large"
                    class="flex items-center justify-center"
                    @click="showJsonManagement = true"
                    title="View/Export JSON"
                    :disabled="
                      (selectedAnalysisExecutionId == null ||
                        isLoadingAvailableTables ||
                        isLoadingAnalysisFile ||
                        isLoadingPkResultsFile ||
                        isLoadingPkResultsCustomCalcsFile ||
                        isLoadingFileContent) &&
                      (activeTables.length === 0 || isProcessingTables)
                    "
                  >
                    <CodeOutlined class="text-base" style="line-height: 1; margin: 0" />
                  </Button>
                </a-tooltip>
              </div>
            </div>
          </div>

          <div class="tables-list-container flex-1 p-4">
            <!-- Tables List -->
            <div class="tables-scroll-area min-h-0 flex-1 overflow-y-auto px-1 py-2">
              <!-- Empty State -->
              <div
                v-if="activeTables.length === 0"
                class="flex h-full flex-col items-center justify-start py-12 text-center"
              >
                <div class="mb-6">
                  <div
                    class="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-[var(--from-blue-gradient)] to-[var(--to-blue-gradient)]"
                  >
                    <svg
                      class="h-10 w-10 text-blue-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                      />
                    </svg>
                  </div>
                </div>
                <h3 class="mb-2 text-lg font-semibold">No Tables Yet</h3>
                <!-- Quick Tips -->
                <div
                  class="mt-8 max-w-md rounded-lg border bg-[var(--card-lighter)] p-4 dark:bg-card"
                >
                  <h4 class="mb-2 text-sm font-medium text-blue-900 dark:text-white">
                    💡 Quick Tips
                  </h4>
                  <ul class="space-y-1 text-xs text-blue-800 dark:text-white">
                    <li>• Tables are created and configured after selecting a file</li>
                    <li>• Use the + button to add a table type</li>
                    <li>• Configure each table in the right panel</li>
                  </ul>
                </div>
              </div>
              <div v-else class="space-y-1.5 px-1 pb-5">
                <Card
                  v-for="(table, index) in activeTables"
                  :key="table.id"
                  :class="[
                    'group cursor-pointer bg-[var(--card)] transition-all duration-200 hover:shadow-md dark:bg-[var(--card-light)]',
                    {
                      'ring-2 ring-blue-500': selectedTable?.id === table.id,
                      'opacity-50': draggedTable?.id === table.id,
                      'border-green-300 bg-[var(--card-light)] dark:bg-[var(--card-lighter)]':
                        dragOverIndex === index,
                      'rule-moved-highlight': highlightedTableId === table.id,
                      'rule-deleting': deletingTableId === table.id
                    }
                  ]"
                  :bodyStyle="{
                    padding: '18px'
                  }"
                  draggable="true"
                  @click="selectTable(table)"
                  @dragstart="handleDragStart($event, table)"
                  @dragover="handleDragOver($event, index)"
                  @dragleave="handleDragLeave"
                  @drop="handleDrop($event, index)"
                  @dragend="handleDragEnd"
                >
                  <div>
                    <div class="flex items-start justify-between">
                      <!-- Drag Handle -->
                      <div class="mr-3 flex items-center">
                        <HolderOutlined class="cursor-grab text-gray-400 active:cursor-grabbing" />
                      </div>
                      <div class="min-w-0 flex-1">
                        <h4 class="truncate text-sm font-medium leading-tight">
                          {{ table.display_name }}
                        </h4>
                        <p class="mt-0 line-clamp-1 text-xs text-gray-600">
                          {{ table.configuration?.description ?? '&nbsp;' }}
                        </p>
                      </div>
                      <!-- Table Controls -->
                      <div
                        class="ml-1.5 flex flex-row gap-1 opacity-0 transition-opacity group-hover:opacity-100"
                      >
                        <Button
                          type="text"
                          size="small"
                          :disabled="activeTables.findIndex((t) => t.id === table.id) === 0"
                          class="flex h-5 w-5 items-center justify-center"
                          @click.stop="moveTableUp(table)"
                        >
                          <ArrowUpOutlined class="text-xs" />
                        </Button>
                        <Button
                          type="text"
                          size="small"
                          :disabled="
                            activeTables.findIndex((t) => t.id === table.id) ===
                            activeTables.length - 1
                          "
                          class="flex h-5 w-5 items-center justify-center"
                          @click.stop="moveTableDown(table)"
                        >
                          <ArrowDownOutlined class="text-xs" />
                        </Button>
                        <Button
                          type="link"
                          size="small"
                          danger
                          class="flex h-8 w-8 items-center justify-center"
                          @click.stop="removeTable(table)"
                        >
                          <Trash2 class="text-base" style="line-height: 1; margin: 0" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </SplitterPanel>
      <SplitterPanel style="flex-basis: calc(100% - 4px)" class="bg-white dark:bg-[var(--card)]">
        <div :class="['flex', 'flex-col', { 'h-full': isFitToBrowser }]">
          <div
            class="flex items-center border-b bg-[var(--card-lighter)] p-4 dark:bg-[var(--card)]"
          >
            <div class="mr-5 min-w-0 flex-1">
              <h3 class="text-nowrap text-xs font-semibold sm:text-sm md:text-base lg:text-base">
                {{
                  selectedTable
                    ? `Configure: ${selectedTable.display_name}`
                    : 'Tables Configuration'
                }}
              </h3>
              <p class="mt-100 text-xs sm:text-sm md:text-sm lg:text-sm">
                {{
                  selectedAnalysisExecutionId != null
                    ? selectedTable != null
                      ? 'Configure the selected table parameters below'
                      : 'Select a table from the left panel to configure its settings'
                    : 'Select an analysis to begin configuring tables'
                }}
              </p>
            </div>
            <slot v-if="wizardModel" name="wizardNavigation" />

            <div v-else class="flex items-center gap-1">
              <div class="mr-3">
                <ExecutionStatusComponent
                  :execution="execution"
                  :show-run-time="true"
                  ref="executionStatusComponent"
                  @notify-execution-status-changed="onExecutionStatusChanged"
                  @notify-execution-complete="onExecutionCompleted"
                />
              </div>

              <!-- Download Results -->
              <a-tooltip>
                <template #title>
                  {{
                    !execution?.id || !activeTables?.length
                      ? 'Generate Tables to download results.'
                      : 'Download Results'
                  }}
                </template>
                <Button
                  v-if="isExtraLargeTierOrLess"
                  type="primary"
                  shape="circle"
                  size="large"
                  class="flex items-center justify-center"
                  :disabled="downloadResultsButtonDisabled"
                  @click="downloadResults"
                >
                  <span class="ant-button-flex-content">
                    <Loader2 v-if="isDownloadingResults" class="size-4 animate-spin" />
                    <Download
                      v-else
                      class="size-5"
                      stroke-width="1.5"
                      :class="{
                        color: !downloadResultsButtonDisabled ? 'bg-[var(--background)]' : undefined
                      }"
                    />
                  </span>
                </Button>

                <Button
                  v-else
                  type="primary"
                  :disabled="downloadResultsButtonDisabled"
                  @click="downloadResults"
                >
                  <span class="ant-button-flex-content">
                    <Loader2 v-if="isDownloadingResults" class="mr-2 size-4 animate-spin" />
                    <Download
                      v-else
                      class="size-5"
                      stroke-width="1.5"
                      :class="{
                        color: !downloadResultsButtonDisabled ? 'bg-[var(--background)]' : undefined
                      }"
                    />
                    {{ 'Download Results' }}
                  </span>
                </Button>
              </a-tooltip>

              <a-tooltip
                placement="left"
                :open="
                  selectedAnalysisExecutionId == null || !activeTables?.length || isProcessingTables
                    ? false
                    : undefined
                "
              >
                <template #title>
                  {{
                    selectedAnalysisExecutionId == null || !activeTables?.length
                      ? 'Select an analysis and add tables to Generate Tables.'
                      : !isProcessingTables
                        ? 'Generate Tables'
                        : 'Generating Tables'
                  }}
                </template>
                <Button
                  v-if="isExtraLargeTierOrLess"
                  type="primary"
                  shape="circle"
                  size="large"
                  class="flex items-center justify-center"
                  :disabled="
                    selectedAnalysisExecutionId == null ||
                    !activeTables?.length ||
                    isProcessingTables
                  "
                  @click="onSubmitTablesForGenerationClicked"
                >
                  <Loader2 v-if="isProcessingTables" class="size-4 animate-spin" />
                  <TableIcon v-else class="h-4 w-4" />
                </Button>
                <Button
                  v-else
                  type="primary"
                  :disabled="
                    selectedAnalysisExecutionId == null ||
                    !activeTables?.length ||
                    isProcessingTables
                  "
                  @click="onSubmitTablesForGenerationClicked"
                >
                  <span class="ant-button-flex-content">
                    <Loader2 v-if="isProcessingTables" class="size-4 animate-spin" />
                    <TableIcon v-else class="h-4 w-4" />
                    {{ 'Generate Tables' }}
                  </span>
                </Button>
              </a-tooltip>
            </div>
          </div>
          <div
            v-if="
              (isLoadingAvailableTables ||
                isLoadingAnalysisFile ||
                isLoadingPkResultsFile ||
                isLoadingPkResultsCustomCalcsFile ||
                isLoadingFileContent) &&
              !wizardModel
            "
            class="flex h-full items-center justify-center"
          >
            <div :class="['py-8', 'text-center', { 'mt-[150px]': !isFitToBrowser }]">
              <Spin size="large" />
              <p class="mt-2 text-gray-500">Loading...</p>
            </div>
          </div>
          <div v-else class="flex min-h-0 flex-1 flex-col p-4">
            <p
              v-if="selectedAnalysisExecutionId == null && !hasErrorLoadingTables"
              class="mt-8 text-center text-sm text-gray-600"
            >
              Select an analysis to begin configuring tables
            </p>
            <div v-else-if="hasErrorLoadingTables" class="mt-8 flex justify-center text-red-400">
              <span class="anticon anticon-warning-filled mr-2 text-2xl" />
              An error was encountered loading tables.
            </div>

            <div
              v-else-if="selectedTable != null && uiFieldGroups != null && uiFieldGroups?.length"
              class="min-h-0 flex-1"
            >
              <a-tabs
                :key="selectedTable?.id"
                v-model:activeKey="activeTableTabKey"
                @change="onTableViewModeTabChange"
                :destroyInactiveTabPane="true"
              >
                <a-tab-pane
                  v-for="(fieldGroup, index) in uiFieldGroups"
                  :key="fieldGroup.name"
                  :tab="fieldGroup.name"
                >
                  <TableConfiguration
                    :configuration-level="1"
                    :table="selectedTable"
                    :root-table="selectedTable"
                    :ui-field-group="fieldGroup"
                    :available-columns="availableColumns"
                    :available-params="availableParams"
                    :available-data-sources="availableDataSources"
                    :selected-data-source="selectedDataSource"
                    @configuration-updated="handleConfigurationUpdate"
                    @data-source-selected="handleDataSourceSelected"
                    @generating-rows="handleGeneratingRowsChange"
                    :table-schemas="availableTableSchemas"
                    :show-description="index === 0"
                    :is-condensed-layout="true"
                    :wizard-model="wizardModel"
                  />
                </a-tab-pane>
                <a-tab-pane
                  key="edit"
                  tab="JSON"
                  :disabled="
                    TablesTypeGuardService.isPayloadTemplateTableSchema(selectedTable.schema)
                  "
                >
                  <div class="mt-[20px]">
                    <JsonTableConfiguration
                      v-if="activeTableEditConfiguration != null"
                      :key="selectedTable?.id"
                      :table-schemas="availableTableSchemas"
                      :available-columns="availableColumns"
                      v-model="activeTableEditConfiguration"
                      :editor-schema="
                        TablesJsonTableConfigurationService.getJsonEditorSchemaForTable()
                      "
                      :place-holder="'Configure JSON'"
                      :configuration-level="1"
                      @update:modelValue="handleJsonEditorConfigurationChange"
                      :allow-type-change="false"
                    />
                  </div>
                </a-tab-pane>
              </a-tabs>
            </div>

            <a-tabs
              v-else-if="selectedTable != null"
              @change="onTableViewModeTabChange"
              :key="selectedTable?.id"
              v-model:activeKey="activeTableViewMode.mode"
              :destroyInactiveTabPane="true"
            >
              <a-tab-pane key="view" tab="Input" :disabled="inputTabDisabled">
                <TableConfiguration
                  :configuration-level="1"
                  :table="selectedTable"
                  :root-table="selectedTable"
                  :available-columns="availableColumns"
                  :available-params="availableParams"
                  :available-data-sources="availableDataSources"
                  @configuration-updated="handleConfigurationUpdate"
                  @data-source-selected="handleDataSourceSelected"
                  :selected-data-source="selectedDataSource"
                  :table-schemas="availableTableSchemas"
                  :show-description="true"
                  :is-condensed-layout="true"
                  :wizard-model="wizardModel"
                />
              </a-tab-pane>
              <a-tab-pane
                key="edit"
                tab="JSON"
                :disabled="
                  TablesTypeGuardService.isPayloadTemplateTableSchema(selectedTable.schema)
                "
              >
                <div class="mt-[20px]">
                  <JsonTableConfiguration
                    v-if="activeTableEditConfiguration != null"
                    :key="selectedTable?.id"
                    :table-schemas="availableTableSchemas"
                    :available-columns="availableColumns"
                    v-model="activeTableEditConfiguration"
                    :editor-schema="
                      TablesJsonTableConfigurationService.getJsonEditorSchemaForTable()
                    "
                    :place-holder="'Configure JSON'"
                    :configuration-level="1"
                    @update:modelValue="handleJsonEditorConfigurationChange"
                    :allow-type-change="false"
                  />
                </div>
              </a-tab-pane>
            </a-tabs>
          </div>
        </div>
      </SplitterPanel>
    </Splitter>

    <!-- Table Selection Modal -->
    <TableSelectionModal
      v-model:visible="showTableSelection"
      :table-schemas="availableTableSchemas"
      @table-selected="addTable"
    />

    <!-- Analysis Selection Modal -->
    <Modal
      v-model:open="showAnalysisSelectionModal"
      title="Select an Analysis"
      width="80%"
      :bodyStyle="{ height: '60vh' }"
      :footer="null"
      centered
      destroyOnClose
    >
      <div style="overflow-y: auto; height: 60vh">
        <ExecutionSelectionTable
          @execution-selected="handleAnalysisExecutionSelected"
          :execution-type-filter="[ExecutionPhaseType.NCAAnalysis]"
        />
      </div>
    </Modal>

    <!-- JSON Export Drawer -->
    <Drawer
      v-model:open="showJsonManagement"
      :destroy-on-close="true"
      title="Tables JSON Viewer"
      placement="right"
      :width="800"
      :closable="true"
      :mask-closable="true"
    >
      <TableJsonManagement
        v-if="parentExecutionId != null && availableTableSchemas != null"
        :key="selectedTable?.id"
        :table-schemas="availableTableSchemas"
        :parent-execution-id="parentExecutionId"
        v-model:execution-name="executionName"
        :tables="activeTables"
        :visibility-changed="showJsonManagement"
        :tables-json-parsing-service="tablesJsonParsingService"
        @tables-applied="onJsonManagementTablesApplied"
      />
    </Drawer>
  </div>
</template>

<style scoped>
  /* make the button a flexible item and allow it to shrink in a flex row */
  .file-select-button {
    text-align: center;
    flex: 1 1 0; /* take remaining space but allow shrinking */
    min-width: 0; /* CRITICAL: allow shrinking so ellipsis can take effect */
  }

  /* Ensure the Ant button inner wrapper behaves as a constrained block */
  .file-select-button .ant-btn-inner,
  .file-select-button .file-select-label {
    display: block; /* give the element a width */
    width: 100%; /* constrain to button width */
    overflow: hidden; /* required for ellipsis */
    white-space: nowrap; /* required for ellipsis */
    text-overflow: ellipsis; /* show "..." when truncated */
  }

  .tables-container {
    display: flex;
    flex-direction: column;
    min-height: 0;
    /* Allow component to fill available space via flex but don't force parent to grow */
  }

  /* apply flex fill only when layout mode requires filling the browser viewport */
  .tables-fill {
    flex: 1 1 0;
  }

  /* left list container becomes a flex column and will handle scrolling itself */
  .tables-list-container {
    display: flex;
    flex-direction: column;
    /* default (FitToContent): let content size naturally and allow vertical growth */
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
  }
  /* full-height behavior only when fit-to-browser */
  .fit-to-browser .tables-list-container {
    height: 100%;
    max-height: 100%;
    overflow-y: auto;
  }

  .tables-scroll-area {
    min-height: 0;
    /* let outer container handle scrolling to avoid nested scrollbars */
    overflow: visible;
  }

  .selected-table {
    border: 2px solid #1890ff;
  }
  .drop-zone-active {
    height: 4px;
    background-color: rgba(37, 99, 235, 0.2);
    transition: height 0.2s ease;
  }
  .table-moved-highlight {
    animation: tableMovedPulse 1.5s ease-in-out;
    border: 2px solid #10b981 !important;
    background-color: #ecfdf5 !important;
    box-shadow: 0 0 0 4px rgba(16, 185, 129, 0.1) !important;
  }
  @keyframes tableMovedPulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.4);
    }
    50% {
      transform: scale(1.02);
      box-shadow: 0 0 0 8px rgba(16, 185, 129, 0.2);
    }
    100% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
    }
  }
  .table-deleting {
    animation: tableDeletePulse 0.6s ease-in-out;
    border: 2px solid #ef4444 !important;
    background-color: #fef2f2 !important;
    box-shadow: 0 0 0 4px rgba(239, 68, 68, 0.1) !important;
  }
  @keyframes tableDeletePulse {
    0% {
      transform: scale(1);
      box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4);
    }
    25% {
      transform: scale(1.02);
      box-shadow: 0 0 0 6px rgba(239, 68, 68, 0.3);
    }
    75% {
      transform: scale(0.98);
      box-shadow: 0 0 0 8px rgba(239, 68, 68, 0.2);
      opacity: 0.7;
    }
    100% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
      opacity: 0;
    }
  }
  .radio-icon-center {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    font-size: 18px;
  }

  /* Ensure splitter fills available vertical space and panels allow inner scrolling */
  :deep(.p-splitter) {
    border: none;
    height: auto;
  }
  .fit-to-browser :deep(.p-splitter) {
    height: 100%;
  }
  :deep(.p-splitter-panel) {
    display: flex;
    flex-direction: column;
    min-height: 0;
  }

  /* Only apply full-height ant-tabs behavior when FitToBrowser is active */
  .fit-to-browser :deep(.ant-tabs) {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }
  .fit-to-browser :deep(.ant-tabs .ant-tabs-nav) {
    flex: 0 0 auto;
  }
  .fit-to-browser :deep(.ant-tabs .ant-tabs-content-holder),
  .fit-to-browser :deep(.ant-tabs .ant-tabs-content) {
    flex: 1 1 auto;
    min-height: 0 !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch;
  }
  .fit-to-browser :deep(.ant-tabs .ant-tabs-tabpane) {
    min-height: 0;
    height: 100%;
    overflow-y: auto;
  }
</style>
